import { ApolloClient } from '@apollo/client';
import { useEffect } from 'react';
import { wsEventBus } from 'src/core/realtime-connection';
import { GET_ENTITY_VIEWER_QUERY } from 'src/graphql/entities/entity-viewer/entity-viewer.operations';
import {
  EntityViewer,
  EntityViewerStatus,
  GetEntityViewerResult,
  GetEntityViewerVars,
} from 'src/graphql/entities/entity-viewer/entity-viewer.types';

enum EventHubCustomEventType {
  ViewersPing = 'ViewersPing',
}

interface ViewersPingEvent {
  entity: string;
  entityId: string;
  viewerId: string;
  ts: number;
  status: EntityViewerStatus;
  expiresAt: number;
  firstName?: string;
  lastName?: string;
}

const isActiveNow = (expiresAt: number, status: string) => {
  const now = Math.floor(Date.now() / 1000);
  return status === 'active' && now <= expiresAt;
};

export const useEntityViewersSubscription = (
  client: ApolloClient<unknown>,
  variables: GetEntityViewerVars,
) => {
  const canSubscribe = !!(variables.params?.entity && variables.params?.entityId);

  useEffect(() => {
    if (!canSubscribe) return;

    const handleSingleViewer = (event: ViewersPingEvent) => {
      if (
        event.entity !== variables.params.entity ||
        event.entityId !== variables.params.entityId
      ) {
        return;
      }

      client.cache.updateQuery<GetEntityViewerResult, GetEntityViewerVars>(
        { query: GET_ENTITY_VIEWER_QUERY, variables },
        (cachedData) => {
          if (!cachedData) return cachedData;

          const currentItems = cachedData.getEntityViewer.items;

          const idx = currentItems.findIndex((v) => v.viewerId === event.viewerId);
          const prev = idx > -1 ? currentItems[idx] : undefined;

          const newViewer = {
            __typename: 'EntityViewerItem',
            viewerId: event.viewerId,
            firstName: event.firstName,
            lastName: event.lastName,
            expiresAt: event.expiresAt,
            lastSeen:
              event.status === EntityViewerStatus.inactive
                ? (prev?.lastSeen ?? event.ts)
                : event.ts,
            status: isActiveNow(event.expiresAt, event.status)
              ? EntityViewerStatus.active
              : EntityViewerStatus.inactive,
          };

          const viewerIndex = currentItems.findIndex((v) => v.viewerId === newViewer.viewerId);

          let updatedItems: EntityViewer[];
          if (viewerIndex > -1) {
            // viewer exists, just update their lastSeen time.
            updatedItems = [...currentItems];
            updatedItems[viewerIndex] = {
              ...updatedItems[viewerIndex],
              ...newViewer,
            };
          } else {
            // new viewer has appeared, add them to the list.
            updatedItems = [...currentItems, newViewer];
          }

          return {
            getEntityViewer: {
              ...cachedData.getEntityViewer,
              items: updatedItems,
              ts: event.ts,
            },
          };
        },
      );
    };

    const singleViewerSub = wsEventBus
      // @ts-expect-error temp, need to adjust ws event bus to support new types
      .onOperation(EventHubCustomEventType.ViewersPing)
      // @ts-expect-error temp, need to adjust ws event bus to support new types
      .subscribe({ next: (e) => handleSingleViewer(e as ViewersPingEvent) });

    return () => {
      singleViewerSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, canSubscribe, variables.params?.entity, variables.params?.entityId]);
};
